import React from "react";
import "assets/css/custom.css";
import { PAGE_SIZE_OPTIONS } from "utils/helpers";

const Pagination = ({
  rowsPerPage = 20,
  currentPage = 0,
  totalRows = 0,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = PAGE_SIZE_OPTIONS,
  maxVisiblePages,
  t = (key) => key,
}) => {
  const [screenWidth, setScreenWidth] = React.useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );

  React.useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const safeRowsPerPage = Number(rowsPerPage) || 5;
  const safeCurrentPage = Number(currentPage) || 0;
  const safeTotalRows = Number(totalRows) || 0;
  const totalPages = Math.ceil(safeTotalRows / safeRowsPerPage);

  const actualMaxVisiblePages =
    screenWidth < 768 ? 4 : maxVisiblePages || 5;

  React.useEffect(() => {
    if (safeCurrentPage >= totalPages && totalPages > 0) {
      onPageChange(0);
    } else if (totalPages === 0 && safeCurrentPage > 0) {
      onPageChange(0);
    }
  }, [
    safeTotalRows,
    safeRowsPerPage,
    totalPages,
    safeCurrentPage,
    onPageChange,
  ]);

  const getVisiblePageNumbers = () => {
    let startPage = Math.max(
      0,
      safeCurrentPage - Math.floor(actualMaxVisiblePages / 2)
    );
    let endPage = startPage + actualMaxVisiblePages;
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(0, endPage - actualMaxVisiblePages);
    }

    return Array.from(
      { length: endPage - startPage },
      (_, i) => startPage + i
    );
  };

  const firstEntry =
    safeTotalRows === 0 ? 0 : safeCurrentPage * safeRowsPerPage + 1;

  const lastEntry = Math.min(
    (safeCurrentPage + 1) * safeRowsPerPage,
    safeTotalRows
  );

  return (
    <div className="pagination-container d-flex justify-content-between align-items-center mx-4 pt-3 pb-2">
      <div className="d-flex align-items-center gap-2">
        <div className="d-flex align-items-center">
          <span className="text-sm">{t("table.pagination.show", "Show")}</span>
          <select
            className="pagination-select-root mx-2"
            value={safeRowsPerPage}
            onChange={(e) => {
              onRowsPerPageChange(Number(e.target.value));
              onPageChange(0);
            }}
          >
            {rowsPerPageOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-sm">{t("table.pagination.entries", "entries")}</span>
        </div>

        <span className="text-sm ml-2">
          {t("table.pagination.showing", "Showing")} {firstEntry} {t("table.pagination.to", "to")}{" "}
          {lastEntry} {t("table.pagination.of", "of")} {safeTotalRows}
        </span>
      </div>

      <div className="d-flex align-items-center gap-1">
        <div
          className={`rounded-circle-div border p-2 ${
            safeCurrentPage === 0 ? "opacity-50" : ""
          }`}
          onClick={() =>
            safeCurrentPage > 0 && onPageChange(safeCurrentPage - 1)
          }
        >
          &lt;
        </div>

        {getVisiblePageNumbers().map((pageNumber) => (
          <div
            key={pageNumber}
            className={`rounded-circle-div border p-2 ml-1 ${
              pageNumber === safeCurrentPage
                ? "bg-primary text-white"
                : "opacity-50"
            }`}
            onClick={() => onPageChange(pageNumber)}
          >
            {pageNumber + 1}
          </div>
        ))}

        <div
          className={`rounded-circle-div border p-2 ml-1 ${
            safeCurrentPage === totalPages - 1 ? "opacity-50" : ""
          }`}
          onClick={() =>
            safeCurrentPage < totalPages - 1 &&
            onPageChange(safeCurrentPage + 1)
          }
        >
          &gt;
        </div>
      </div>
    </div>
  );
};

export default Pagination;
